import React, { useState } from 'react';
import { Box, Button, Grid2, Tab, Tabs } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { useTranslation } from 'react-i18next';
import PageWithTitle from '../../components/atom/PageWithTitle';
import useNotify from '../../hooks/useNotify';
import useSorting from '../../hooks/useSorting';
import { useGetAffiliateInvoices } from '@openapi/invoices/invoices';
import InvoiceHistoryTable from './InvoiceHistoryTable';
import { useCapabilities } from '../../hooks/useCapabilities';
import { useModals, MODAL_TYPE } from '../../hooks/useModal';

const INITIAL_FILTERS = {
    venueId: '',
    showId: '',
    commissionGroupId: '',
    day: '',
    from: '',
    to: '',
};

function InvoicesPage() {
    const { t } = useTranslation();
    const { can, CAPABILITIES } = useCapabilities();
    const canGenerateInvoices = can(CAPABILITIES.INVOICES_VIEW);
    const { notifyError } = useNotify();
    const { showModal } = useModals();

    const [filters, setFilters] = useState(INITIAL_FILTERS);
    const { sort, sortDir, setSorting } = useSorting(['id', 'DESC']);

    const {
        data: invoicesData,
        isLoading: loadingInvoices,
        error: invoicesError,
        refetch: reloadInvoices,
    } = useGetAffiliateInvoices(
        {
            venueId: filters.venueId || undefined,
            showId: filters.showId || undefined,
            commissionGroupId: filters.commissionGroupId || undefined,
            from: filters.day || filters.from || undefined,
            to: filters.day || filters.to || undefined,
            includeDeleted: false,
            sort,
            sortDir,
        },
        { query: { onError: notifyError } },
    );
    const invoices = invoicesData?.invoices || [];

    const [directionTab, setDirectionTab] = useState('receivable');
    // A mixed commission group is invoiced as two separate documents (receivable
    // and payable); the tabs keep the two directions apart. The receivable tab
    // also shows legacy invoices (direction === null) generated before the split,
    // since those were receivable-style documents.
    const visibleInvoices = invoices.filter((invoice) =>
        directionTab === 'payable'
            ? invoice.direction === 'payable'
            : invoice.direction !== 'payable',
    );

    const openGenerateModal = () => {
        showModal(MODAL_TYPE.GENERATE_INVOICE, {
            onGenerated: reloadInvoices,
        });
    };

    return (
        <PageWithTitle
            title={t('invoices.title')}
            headline={
                canGenerateInvoices
                    ? [
                          <Button
                              key="add-invoice"
                              variant="contained"
                              color="secondary"
                              startIcon={<AddIcon />}
                              onClick={openGenerateModal}
                              sx={{ textTransform: 'none' }}
                          >
                              {t('invoices.addInvoice')}
                          </Button>,
                      ]
                    : []
            }
        >
            <Grid2 size={{ xs: 12 }}>
                <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
                    <Tabs value={directionTab} onChange={(_event, value) => setDirectionTab(value)}>
                        <Tab value="receivable" label={t('invoices.tabReceivable')} />
                        <Tab value="payable" label={t('invoices.tabPayable')} />
                    </Tabs>
                </Box>
                <InvoiceHistoryTable
                    invoices={visibleInvoices}
                    loadingInvoices={loadingInvoices}
                    invoicesError={invoicesError}
                    reloadInvoices={reloadInvoices}
                    filters={filters}
                    setFilters={setFilters}
                    clearFilters={() => setFilters(INITIAL_FILTERS)}
                    sort={sort}
                    sortDir={sortDir}
                    setSorting={setSorting}
                />
            </Grid2>
        </PageWithTitle>
    );
}

export default InvoicesPage;
